import React, { useState, useRef } from 'react';
import { X, ClipboardList, ZoomIn, Copy, Printer, Loader2 } from 'lucide-react';
import config from '../../config/config';
import useFastImageCopy from '../../hooks/useFastImageCopy';

const NumuneOnizlemeModal = ({ isOpen, onClose, numune }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const modalRef = useRef(null);
  const modalContentRef = useRef(null);

  const numuneNo = numune?.numune_no || '';

  const { preparing, copyImage, shareWhatsApp, printImage } = useFastImageCopy({
    isOpen,
    ready: !!numune,
    targetRef: modalContentRef,
    fileName: `numune-${numuneNo || 'preview'}.png`,
    successMessage: 'Numune panoya kopyalandı',
    printTitle: `Numune Detayı - ${numuneNo || 'Numune'}`,
    shareTitle: 'Numune Detayı',
    shareText: `Numune No: ${numuneNo}`
  });

  if (!isOpen || !numune) return null;

  const processAllImages = (gorselUrls) => {
    if (!gorselUrls) return [];
    
    let urls = gorselUrls;
    if (typeof urls === 'string') {
      try {
        urls = JSON.parse(urls);
      } catch (e) {
        urls = [urls];
      }
    }
    
    if (Array.isArray(urls)) {
      return urls
        .map((url) => {
          const cleanUrl = String(url || '').replace(/^["']+|["']+$/g, '');
          return cleanUrl.startsWith('http') ? cleanUrl : `${config.backendUrl}${cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`}`;
        })
        .filter(Boolean);
    }
    
    return [];
  };

  let desenImages = [];
  if (numune.desen?.gorsel_urls) {
    desenImages = processAllImages(numune.desen.gorsel_urls);
  }

  const primaryImage = desenImages[selectedImageIndex] || desenImages[0] || null;

  const handleImageClick = (imageUrl) => {
    setEnlargedImage(imageUrl);
  };

  const handleCloseEnlarged = () => {
    setEnlargedImage(null);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full" ref={modalRef}>
          <div className="flex justify-end mb-2 gap-1 p-2">
            <button
              onClick={copyImage}
              disabled={preparing}
              className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              title={preparing ? 'Kopyalanıyor...' : 'Panoya Kopyala'}
            >
              {preparing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
            
            <button
              onClick={printImage}
              className="p-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors shadow-lg"
              title="Yazdır"
            >
              <Printer className="h-4 w-4" />
            </button>
            
            <button
              onClick={shareWhatsApp}
              disabled={preparing}
              className="p-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              title="WhatsApp'ta Paylaş"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
              </svg>
            </button>

            <button
              onClick={onClose}
              className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors shadow-lg"
              title="Kapat"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg" ref={modalContentRef}>
            <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <ClipboardList className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Numune Detayları
                </h3>
              </div>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                <div className="md:col-span-2">
                  <div 
                    className="bg-gray-100 dark:bg-gray-700 rounded-lg h-72 md:h-full flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-90 transition-opacity relative group"
                    onClick={() => primaryImage && handleImageClick(primaryImage)}
                  >
                    {primaryImage ? (
                      <>
                        <img
                          src={primaryImage}
                          alt={numune.desen?.desen_no || 'Numune görseli'}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <ZoomIn className="h-8 w-8 text-white" />
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center text-gray-400 dark:text-gray-500">
                        <ClipboardList className="h-12 w-12" />
                        <p className="mt-2 text-sm">Görsel bulunmuyor</p>
                      </div>
                    )}
                  </div>
                  {desenImages.length > 1 && (
                    <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                      {desenImages.map((url, index) => (
                        <div
                          key={index}
                          className={`relative flex-shrink-0 cursor-pointer transition-all ${
                            index === selectedImageIndex 
                              ? 'ring-2 ring-purple-500 ring-offset-2' 
                              : 'hover:opacity-75'
                          }`}
                          onClick={() => {
                            setSelectedImageIndex(index);
                          }}
                        >
                          <img
                            src={url}
                            alt={`Desen ${index + 1}`}
                            className="w-20 h-20 object-cover rounded-md border-2 border-gray-200 dark:border-gray-600"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          {index === selectedImageIndex && (
                            <div className="absolute inset-0 bg-purple-500 bg-opacity-20 rounded-md" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="md:col-span-3 space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Numune No</span>
                      <span className="text-lg font-semibold text-gray-900 dark:text-white">{numune.numune_no ? numune.numune_no.toUpperCase() : '-'}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Müşteri
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{numune.musteri_adi}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Durum
                      </label>
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                        numune.durum === 'Aktif'
                          ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'
                          : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                      }`}>
                        {numune.durum}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Stok Kartı
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{numune.stok_kart_adi || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Desen
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{numune.desen?.desen_no || '-'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Miktar
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {`${Number(numune.miktar || 0).toFixed(1)} ${numune.birim || 'm'}`}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Oluşturulma Tarihi
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {new Date(numune.created_at).toLocaleDateString('tr-TR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>

                  {numune.notlar && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Notlar
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{numune.notlar}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                onClick={onClose}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-purple-600 text-base font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      </div>

      {enlargedImage && (
        <div 
          className="fixed inset-0 z-[60] bg-black bg-opacity-90 flex items-center justify-center p-4"
          onClick={handleCloseEnlarged}
        >
          <button
            onClick={handleCloseEnlarged}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
          >
            <X className="h-8 w-8" />
          </button>
          <img
            src={enlargedImage}
            alt="Büyütülmüş görsel"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default NumuneOnizlemeModal;
